import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Building2, Briefcase, ArrowRight, TrendingUp, Clock, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const caseStudies = [
  {
    sector: 'Personal Lines',
    icon: Car,
    color: 'sky',
    title: 'Multi-State Personal Auto Implementation',
    client: 'Leading Regional Insurer',
    description: 'Complete PolicyCenter implementation supporting personal auto across 15 states with integrated rating and agency portal.',
    stats: [
      { value: '60%', label: 'Faster Quote-to-Bind' },
      { value: '$2M', label: 'Annual Savings' },
      { value: '15', label: 'States Launched' },
    ],
    challenges: ['Legacy system migration', 'Complex rating requirements', 'Agency integration'],
    solutions: ['Phased migration approach', 'Custom rating engine', 'API-first integration'],
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&h=500&fit=crop',
  },
  {
    sector: 'Commercial Auto',
    icon: Building2,
    color: 'emerald',
    title: 'Commercial Fleet Insurance Platform',
    client: 'National Fleet Insurance Provider',
    description: 'End-to-end Guidewire implementation for commercial fleet insurance with telematics integration and advanced risk analytics.',
    stats: [
      { value: '40%', label: 'Claims Reduction' },
      { value: '3X', label: 'Processing Speed' },
      { value: '10K+', label: 'Fleets Managed' },
    ],
    challenges: ['Telematics data volume', 'Real-time risk assessment', 'Custom workflows'],
    solutions: ['Scalable data architecture', 'AI-powered risk engine', 'Automated underwriting'],
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&h=500&fit=crop',
  },
  {
    sector: 'Specialty Insurance',
    icon: Briefcase,
    color: 'violet',
    title: 'Specialty Lines Digital Transformation',
    client: 'Global Specialty Insurer',
    description: 'Cloud migration and modernization of specialty lines including cyber, E&O, and D&O with advanced analytics.',
    stats: [
      { value: '50%', label: 'Cost Reduction' },
      { value: '99.9%', label: 'System Uptime' },
      { value: '8', label: 'Products Launched' },
    ],
    challenges: ['Complex product structures', 'Regulatory compliance', 'Global deployment'],
    solutions: ['Modular architecture', 'Compliance framework', 'Multi-region cloud setup'],
    image: 'https://images.unsplash.com/photo-1560472355-536de3962603?w=800&h=500&fit=crop',
  },
];

const colorClasses = {
  sky: {
    bg: 'bg-sky-100',
    text: 'text-sky-600',
    border: 'border-sky-200',
    gradient: 'from-sky-500 to-sky-600',
  },
  emerald: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    gradient: 'from-emerald-500 to-emerald-600',
  },
  violet: {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    border: 'border-violet-200',
    gradient: 'from-violet-500 to-violet-600',
  },
};

const CaseStudies = () => {
  return (
    <div data-testid="case-studies-page">
      <SEO {...pageSEO.caseStudies} />
      <PageHeader
        label="Success Stories"
        title="Case Studies"
        description="Explore how we've helped leading insurers transform their operations with Guidewire solutions."
      />

      {/* Case Studies */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
                data-testid={`case-study-${index}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg ${colorClasses[study.color].bg} dark:opacity-80 flex items-center justify-center`}>
                      <study.icon className={`w-5 h-5 ${colorClasses[study.color].text}`} />
                    </div>
                    <span className={`text-sm font-semibold ${colorClasses[study.color].text}`}>
                      {study.sector}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
                    {study.title}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4 font-medium">
                    {study.client}
                  </p>
                  <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                    {study.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {study.stats.map((stat, idx) => (
                      <div key={idx} className={`p-4 rounded-xl ${colorClasses[study.color].bg}/50 dark:bg-slate-800`}>
                        <div className={`text-2xl font-bold ${colorClasses[study.color].text}`}>
                          {stat.value}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Challenges & Solutions */}
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <h5 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Challenges</h5>
                      <ul className="space-y-1">
                        {study.challenges.map((challenge, idx) => (
                          <li key={idx} className="text-sm text-slate-500 dark:text-slate-400 flex items-start gap-2">
                            <span className="w-1 h-1 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Solutions</h5>
                      <ul className="space-y-1">
                        {study.solutions.map((solution, idx) => (
                          <li key={idx} className="text-sm text-slate-500 dark:text-slate-400 flex items-start gap-2">
                            <span className={`w-1 h-1 rounded-full ${colorClasses[study.color].text.replace('text', 'bg')} mt-2 flex-shrink-0`} />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative group">
                    <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[study.color].gradient} rounded-2xl rotate-2 scale-105 opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                    <div className="relative overflow-hidden rounded-2xl">
                      <img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Want to Be Our Next Success Story?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Let's discuss how we can help you achieve similar results with 
              your Guidewire implementation.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8"
              >
                Start Your Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
